import * as React from 'react'
import { View, Text, ScrollView, TouchableOpacity, Linking } from 'react-native'
import styled from '@theme/styled-components'
import theme from '@theme/Theme'

import Root from '@components/root/Root'
import Header from '@components/header/Header'
import Icon from '@components/icon/Icons'

import useI18n from '@i18n/useI18n'
import useNavigation from '@layout/useNavigation'
import useBreakpoint from '@utils/useBreakpoints'

import useReducer from '@store/useReducer'
import * as HelpStore from '@store/help/help'

import api from '@api/api'
import { TutorialPlatform } from '@api/enum'
import Logger from '@utils/logger'

const HelpScreen = () => {
  const i18n = useI18n()
  const navigation = useNavigation()

  const [, currentBreakpoint] = useBreakpoint()
  const isPhone = currentBreakpoint === 'phone'

  const helpVideos = useReducer(HelpStore.store, (s) => s.helpVideos)

  React.useEffect(() => {
    api.help
      .tutorials()
      .then((res) => {
        HelpStore.actions.setHelpVideos(res.tutorials)
      })
      .catch(Logger.error)
  }, [i18n.lang])

  const sections = React.useMemo(
    () =>
      helpVideos
        .filter(
          (v) =>
            v.platform === TutorialPlatform.ALL ||
            (isPhone ? v.platform === TutorialPlatform.MOBILE : v.platform === TutorialPlatform.WEB)
        )
        .reduce((acc, cur) => {
          const category = acc.find((c) => c.category === cur.category)
          if (category) {
            return [...acc.filter((c) => c.category !== cur.category), { ...category, items: [...category.items, cur] }]
          }
          return [...acc, { category: cur.category, items: [cur] }]
        }, [] as { category: string; items: TutorialResponse[] }[])
        .sort((a, b) => a.category.localeCompare(b.category)),
    [helpVideos, isPhone]
  )

  const renderItem = (item: TutorialResponse, index: number, list: TutorialResponse[]) => (
    <View key={`${item.title}-${item.url}`}>
      <Item activeOpacity={0.9} onPress={() => Linking.openURL(item.url)} key={item.url}>
        <Label>{item.title}</Label>
        <Icon color={theme.colors.primary} name="arrow" size={24} />
      </Item>
      {index < list.length - 1 && <Separator key={`${item.url}_separator`} />}
    </View>
  )

  return (
    <Root header={i18n.t('screens.help.title')} fixed onLeft={navigation.goBack}>
      {!isPhone && <Header title={i18n.t('screens.help.title')} />}

      <ScrollContainer scrollIndicatorInsets={{ right: 1 }} bounces={false} keyboardShouldPersistTaps="handled">
        {sections.length > 0 ? (
          sections.map((s) => (
            <ItemContainer key={s.category}>
              <CategoryTitle>{s.category}</CategoryTitle>
              <Items>{s.items.map(renderItem)}</Items>
            </ItemContainer>
          ))
        ) : (
          <MissingInfo>{i18n.t('screens.help.noHelp')}</MissingInfo>
        )}
      </ScrollContainer>
    </Root>
  )
}

export default HelpScreen

const ScrollContainer = styled(ScrollView)`
  flex: 1;
  flex-direction: column;
  padding: ${(props) => (props.theme.breakpoint === 'phone' ? '16px' : '20px 25px')};
`

const Item = styled(TouchableOpacity)`
  flex-direction: row;
  align-items: center;
  padding: 10px 16px;
`

const Label = styled(Text)`
  flex: 1;
  ${(props) => props.theme.fonts.Body};
`

const Separator = styled(View)`
  margin-left: 16px;
  height: 1px;
  background-color: ${(props) => props.theme.colors.disable};
`

const ItemContainer = styled(View)`
  padding: 16px;
`

const Items = styled(View)`
  border-radius: 8px;
  background-color: ${(props) => props.theme.colors.background};
`

const CategoryTitle = styled(Text)`
  ${(props) => props.theme.fonts.Title3};
  margin-bottom: 8px;
`

const MissingInfo = styled(Text)`
  ${(props) => props.theme.fonts.Footnote};
  font-size: 14px;
  color: ${(props) => props.theme.colors.secondaryText};
  ${(props) =>
    props.theme.breakpoint === 'phone'
      ? ` padding: 23px 16px;
          background-color: ${props.theme.colors.background};
          text-align: center;
          font-size: 12px;`
      : `padding: 16px 0px;`}
`
